import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

enum LoginStep {
  EMAIL,
  VERIFICATION,
  REGISTRATION
}

const Login: React.FC = () => {
  const { requestVerification, verifyEmail, login } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState<LoginStep>(LoginStep.EMAIL);
  const [email, setEmail] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [verificationId, setVerificationId] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      // Validate email format
      if (!email.includes('@') || !email.includes('.')) {
        throw new Error('Please enter a valid email address');
      }
      
      // Request verification code
      const verificationId = await requestVerification(email);
      setVerificationId(verificationId);
      setStep(LoginStep.VERIFICATION);
    } catch (err: any) {
      setError(err.response?.data?.email || err.message || 'Failed to send verification code');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleVerificationSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      // Verify email with code
      const verificationId = await verifyEmail(email, verificationCode);
      setVerificationId(verificationId);
      setStep(LoginStep.REGISTRATION);
    } catch (err: any) {
      setError(err.response?.data?.code || err.message || 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    
    try {
      // Complete registration and login
      await login(verificationId);
      navigate('/search');
    } catch (err: any) {
      setError(err.response?.data?.verification_id || err.message || 'Registration failed');
    } finally {
      setIsLoading(false);
    }
  };
  
  return (
    <div className="max-w-md mx-auto bg-white p-8 rounded-xl shadow-sm border border-neutral-200">
      <h1 className="text-2xl font-bold text-center mb-6">
        {step === LoginStep.EMAIL && 'Verify Your Academic Email'}
        {step === LoginStep.VERIFICATION && 'Enter Verification Code'}
        {step === LoginStep.REGISTRATION && 'Complete Registration'}
      </h1>
      
      {error && (
        <div className="bg-red-50 text-red-700 p-3 rounded-md mb-4">
          {error}
        </div>
      )}
      
      {step === LoginStep.EMAIL && (
        <form onSubmit={handleEmailSubmit}>
          <div className="mb-4">
            <label htmlFor="email" className="block text-neutral-700 mb-2">
              Academic Email Address
            </label>
            <input
              id="email"
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.name@university.edu"
              required
            />
            <p className="text-neutral-500 text-sm mt-2">
              We'll send a verification code to this address.
            </p>
          </div>
          
          <button type="submit" className="btn btn-primary w-full" disabled={isLoading}>
            {isLoading ? 'Sending...' : 'Send Verification Code'}
          </button>
        </form>
      )}
      
      {step === LoginStep.VERIFICATION && (
        <form onSubmit={handleVerificationSubmit}>
          <div className="mb-4">
            <p className="text-neutral-600 mb-4">
              We've sent a verification code to <strong>{email}</strong>. 
              Please check your inbox and enter the code below.
            </p>
            <label htmlFor="code" className="block text-neutral-700 mb-2">
              Verification Code
            </label>
            <input
              id="code"
              type="text"
              className="input"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="Enter 6-digit code"
              maxLength={6}
              required
            />
          </div>
          
          <button type="submit" className="btn btn-primary w-full" disabled={isLoading}>
            {isLoading ? 'Verifying...' : 'Verify Code'}
          </button>
          
          <button 
            type="button" 
            className="mt-4 text-neutral-600 hover:text-neutral-800 text-center w-full"
            onClick={() => setStep(LoginStep.EMAIL)}
          >
            Back to Email
          </button>
        </form>
      )}
      
      {step === LoginStep.REGISTRATION && (
        <form onSubmit={handleRegistration}>
          <div className="mb-6">
            <p className="text-neutral-600 mb-4">
              Your email has been verified successfully. Click below to complete registration and 
              start rating PIs anonymously.
            </p>
            <p className="text-neutral-700 font-semibold mb-2">
              Important Privacy Notice:
            </p>
            <p className="text-neutral-600 mb-2">
              We will discard your email address after registration. Only your institution information 
              will be kept to validate your ratings.
            </p>
          </div>
          
          <button type="submit" className="btn btn-primary w-full" disabled={isLoading}>
            {isLoading ? 'Completing Registration...' : 'Complete Anonymous Registration'}
          </button>
        </form>
      )}
    </div>
  );
};

export default Login; 